import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useConfig } from '../../utils/useConfig';
import { useLocation } from 'react-router-dom';
import { Product } from '../../models/Product';

interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  noindex?: boolean;
  canonical?: string;
  schemaData?: Record<string, any>;
  product?: Product;
}

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  noindex = false,
  canonical,
  schemaData,
  product,
}) => {
  const config = useConfig();
  const location = useLocation();

  const currentUrl =
    typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}${location.pathname}`
      : '';

  const siteTitle = title
    ? `${title} | ${config.company.name}`
    : config.company.name;
  const siteDescription = description || config.company.about.shortInfo || '';
  const siteKeywords = keywords || config.meta?.keywords || '';
  const siteImage = image || config.company.logo || '';
  const canonicalUrl = canonical || currentUrl;

  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: config.company.name,
    url: currentUrl,
    logo: config.company.logo,
    ...(config.contact?.email && { email: config.contact.email }),
    ...(config.contact?.phone && { telephone: config.contact.phone }),
    ...(config.contact.address && { address: config.contact.address }),
  };

  // Dodanie danych produktu do schematu, jeśli podane
  let schema: Record<string, any> = baseSchema;

  if (product) {
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description || '',
      image: [
        product.primaryImage,
        ...product.secondaryImages.map(img => img.image)
      ],
      sku: product.serialNumber || product.id, // Fallback na ID jeżeli brak serialNumber
      brand: {
        '@type': 'Brand',
        name: product.producer?.name || config.company.name
      },
      offers: {
        '@type': 'Offer',
        url: canonicalUrl,
        priceCurrency: 'PLN',
        price: product.price.toFixed(2),
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition'
      }
    };

    // Nadpisujemy schema aby zawierało tylko dane produktu (dla spójności z Merchant Center Next)
    schema = productSchema;
  }

  // Połączenie z dodatkowymi danymi schematu (jeśli podane)
  if (schemaData) {
    schema = { ...schema, ...schemaData };
  }

  return (
    <Helmet>
      <title>{siteTitle}</title>
      <meta name="description" content={siteDescription} />
      {siteKeywords && <meta name="keywords" content={siteKeywords as any} />}

      <link rel="canonical" href={canonicalUrl} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      {siteImage && <meta property="og:image" content={siteImage} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      {siteImage && <meta name="twitter:image" content={siteImage} />}

      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default Seo;